import {
  EmptyState,
  KeyValueList,
  Panel,
  type KeyValuePairList,
} from "@validereinc/common-components";
import {
  AttributeSchemaHelpers,
  type InputOutputSchemaPropertyConfigurationType,
  type WorkerVersionType,
} from "@validereinc/domain";
import React, { useMemo } from "react";

const schemaToKeyValueList = (
  schema:
    | Record<string, InputOutputSchemaPropertyConfigurationType>
    | null
    | undefined
): KeyValuePairList => {
  if (!schema || typeof schema !== "object") return [];

  const entries = Object.entries(schema).sort(([keyA, a], [keyB, b]) => {
    const titleA = a.display_name?.trim() || keyA;
    const titleB = b.display_name?.trim() || keyB;
    return titleA.localeCompare(titleB, undefined, { sensitivity: "base" });
  });

  return entries.map(([key, entry]) => {
    const displayName = entry.display_name?.trim();
    const title =
      displayName !== undefined && displayName !== "" ? displayName : key;
    const dataTypeLabel = AttributeSchemaHelpers.getDataTypeForDisplay(
      entry.data_type
    );
    const requiredLabel = entry.is_required ? "Required" : "Optional";

    return {
      title,
      value: `${dataTypeLabel} · ${requiredLabel}`,
    };
  });
};

export const WorkerVersionSchemaPanels = ({
  version,
  noVersionEmptyStateTitle = "Version not found.",
  noVersionEmptyStateSuggestion,
}: {
  version?: WorkerVersionType;
  noVersionEmptyStateTitle?: string;
  noVersionEmptyStateSuggestion?: string;
}) => {
  const inputSchemaData = useMemo(
    () => schemaToKeyValueList(version?.input_schema),
    [version?.input_schema]
  );

  const outputSchemaData = useMemo(
    () => schemaToKeyValueList(version?.output_schema),
    [version?.output_schema]
  );

  return (
    <>
      <Panel title="Inputs">
        {version == null ? (
          <EmptyState
            title={noVersionEmptyStateTitle}
            suggestion={noVersionEmptyStateSuggestion}
          />
        ) : inputSchemaData.length > 0 ? (
          <KeyValueList
            data={inputSchemaData}
            variant="shaded"
            color="neutral"
          />
        ) : (
          <EmptyState title="No input schema." />
        )}
      </Panel>
      <Panel title="Outputs">
        {version == null ? (
          <EmptyState
            title={noVersionEmptyStateTitle}
            suggestion={noVersionEmptyStateSuggestion}
          />
        ) : outputSchemaData.length > 0 ? (
          <KeyValueList
            data={outputSchemaData}
            variant="shaded"
            color="neutral"
          />
        ) : (
          <EmptyState title="No output schema." />
        )}
      </Panel>
    </>
  );
};
